import { useEffect, useState } from "react";

import BindingResult from "./BindingResult";
import Loading from "./Loading";
import PhoneNumberVerification from "./PhoneNumberVerification";
import liffClient from "./services/liffClient";
import phoneBindingApi, {
  PhoneBindingApiError,
} from "./services/phoneBindingApi";
import SmsVerification from "./SmsVerification";

type Step =
  | "checking-status"
  | "phone-number"
  | "sms"
  | "loading"
  | "success"
  | "error"
  | "already-linked";

export default function PhoneBinding() {
  const [step, setStep] = useState<Step>("checking-status");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [debugValue, setDebugValue] = useState<string | null>(null);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [isResendingSms, setIsResendingSms] = useState(false);

  const setErrorDebugInfo = (error?: unknown, apiResponse?: unknown) => {
    const idToken = liffClient.getIdToken();

    const debugLines = [`LINE ID Token:`, idToken ?? "null", ""];

    if (error instanceof PhoneBindingApiError) {
      debugLines.push(
        `Error: ${error.message}`,
        `HTTP Status: ${error.status}`,
        `HTTP Status Text: ${error.statusText || "(empty)"}`,
        `API Response Body:`,
        error.responseBody || "(empty)",
      );
    } else if (error instanceof Error) {
      debugLines.push(`Error: ${error.message}`);
    } else if (error) {
      debugLines.push(`Error: ${String(error)}`);
    }

    if (apiResponse) {
      debugLines.push(`API Response:`, JSON.stringify(apiResponse, null, 2));
    }

    setDebugValue(debugLines.join("\n"));
  };

  useEffect(() => {
    const checkPhoneBindingStatus = async () => {
      try {
        const { isPhoneBound } = await phoneBindingApi.getPhoneBindingStatus();

        setStep(isPhoneBound ? "already-linked" : "phone-number");
      } catch (error) {
        console.error("Failed to check phone binding status:", error);

        setErrorDebugInfo(error);
        setErrorMessage(null);
        setStep("error");
      }
    };

    checkPhoneBindingStatus();
  }, []);

  useEffect(() => {
    document.title = "迪士尼美語世界";
  }, []);

  const handlePhoneNumberSubmit = async (value: string) => {
    if (isSendingSms) return;

    setIsSendingSms(true);

    try {
      const result = await phoneBindingApi.sendSms(value);

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setErrorDebugInfo(undefined, result);
        setStep("error");
        return;
      }

      setPhoneNumber(value);
      setStep("sms");
    } catch (error) {
      console.error("Failed to send SMS:", error);

      setErrorDebugInfo(error);
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleVerify = async (verificationCode: string) => {
    setErrorMessage(null);
    setStep("loading");

    try {
      const result = await phoneBindingApi.verify(
        phoneNumber,
        verificationCode,
      );

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setErrorDebugInfo(undefined, result);
        setStep("error");
        return;
      }

      setStep("success");
    } catch (error) {
      console.error("Failed to verify SMS verification code:", error);

      setErrorDebugInfo(error);
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");
    }
  };

  const handleResend = async () => {
    if (isResendingSms) return false;

    setIsResendingSms(true);

    try {
      const result = await phoneBindingApi.sendSms(phoneNumber);

      if (!result.isSuccess) {
        setErrorMessage(result.message);
        setErrorDebugInfo(undefined, result);
        setStep("error");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Failed to resend SMS:", error);

      setErrorDebugInfo(error);
      setErrorMessage("發生異常，請返回 LINE 後重新嘗試");
      setStep("error");

      return false;
    } finally {
      setIsResendingSms(false);
    }
  };

  if (step === "checking-status") return <Loading />;

  if (step === "already-linked") return <BindingResult type="already-linked" />;

  if (step === "phone-number")
    return (
      <PhoneNumberVerification
        isSubmitting={isSendingSms}
        onSubmit={handlePhoneNumberSubmit}
      />
    );

  if (step === "sms")
    return (
      <SmsVerification
        isResending={isResendingSms}
        onVerify={handleVerify}
        onResend={handleResend}
      />
    );

  if (step === "loading") return <Loading message="帳號綁定中..." />;

  if (step === "success") return <BindingResult type="success" />;

  if (step === "error")
    return (
      <BindingResult
        type="error"
        message={errorMessage}
        debugValue={debugValue}
      />
    );

  return null;
}
